import type { InterfaceToken } from '@morpho-org/dapp-core'
import type { PropsWithStyles } from '@morpho-org/ui-v6/types'
import type { ReactNode } from 'react'

import { isDefined } from '@morpho-org/morpho-ts'
import { Avatar } from '@morpho-org/ui-v6/client'
import { Tag } from '@morpho-org/ui-v6/server'
import { cn, formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'

export interface RecapInteractionProps extends PropsWithStyles {
  amount: bigint
  asset: InterfaceToken
  label: string
  /** Optional node rendered next to the amount (e.g. an estimate warning badge). */
  valueSuffix?: ReactNode
}

const RecapInteraction = ({
  className,
  amount,
  asset,
  label,
  valueSuffix
}: RecapInteractionProps) => {
  const usdValue = asset.toUsd(amount)

  const formattedValue = formatters.commas
    .digits(getDigitsFromPriceAndAmount(asset.price, amount, asset.decimals))
    .trailingZero(!amount)
    .of(amount, asset.decimals)

  const formattedUsdValue = formatters.short
    .smallValuesWithCommas()
    .digits(2)
    .unit('$')
    .of(usdValue, 18)

  const detailedUsdValue = formatters.commas.digits(2).unit('$').of(usdValue, 18)

  return (
    <div className={cn('flex flex-col gap-sm items-stretch pb-2xs', className)}>
      <span className="text-secondary-foreground text-xs">{label}</span>
      {/* Pinned to the USD tag's height, so a priceless asset matches the loading row. */}
      <div className="flex min-h-[26px] items-center justify-between gap-[16px]">
        <div className="gap-2xs flex items-center">
          <span className="text-title-xs text-foreground">{formattedValue}</span>
          <span className="text-title-xs text-muted-foreground">{asset.symbol}</span>
          {isDefined(usdValue) && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Tag variant="bundler">
                  <span className="text-3xs text-foreground" style={{ lineHeight: 'normal' }}>
                    {formattedUsdValue}
                  </span>
                </Tag>
              </TooltipTrigger>
              {!!detailedUsdValue && <TooltipContent>{detailedUsdValue}</TooltipContent>}
            </Tooltip>
          )}
          {valueSuffix}
        </div>
        <Avatar src={asset.logoURI} size={24} padding={0} />
      </div>
    </div>
  )
}

export default RecapInteraction
